import { useEffect, useMemo, useRef, useState } from 'react'

/**
 * useDeviceType
 * Detects device category based on viewport width and updates on resize.
 *
 * Defaults (px):
 * - mobile: 0 - 767
 * - tablet: 768 - 1023
 * - desktop: 1024+
 *
 * @param {Object} [customBreakpoints]
 * @param {number} [customBreakpoints.mobileMax=767]
 * @param {number} [customBreakpoints.tabletMax=1023]
 * @returns {{
 *   deviceType: 'mobile'|'tablet'|'desktop',
 *   isMobile: boolean,
 *   isTablet: boolean,
 *   isDesktop: boolean,
 *   width: number
 * }}
 */
export default function useDeviceType(customBreakpoints = {}) {
    const {
        mobileMax = 767,
        tabletMax = 1023
    } = customBreakpoints

    const getTypeFromWidth = (w) => {
        if (w <= mobileMax) return 'mobile'
        if (w <= tabletMax) return 'tablet'
        return 'desktop'
    }

    const initialWidth = typeof window !== 'undefined' ? window.innerWidth : tabletMax + 1
    const [width, setWidth] = useState(initialWidth)
    const [deviceType, setDeviceType] = useState(getTypeFromWidth(initialWidth))

    // Debounce with rAF + timeout for smoother updates
    const frame = useRef(null)
    const resizeTimer = useRef(null)

    useEffect(() => {
        if (typeof window === 'undefined') return undefined

        const onResize = () => {
            if (frame.current) cancelAnimationFrame(frame.current)
            frame.current = requestAnimationFrame(() => {
                const w = window.innerWidth
                // Minimal debounce to avoid noisy updates on continuous resize
                if (resizeTimer.current) clearTimeout(resizeTimer.current)
                resizeTimer.current = setTimeout(() => {
                    setWidth(w)
                    setDeviceType(getTypeFromWidth(w))
                }, 100)
            })
        }

        // Initialize once on mount (in case SSR or hydration)
        onResize()
        window.addEventListener('resize', onResize)
        return () => {
            window.removeEventListener('resize', onResize)
            if (frame.current) cancelAnimationFrame(frame.current)
            if (resizeTimer.current) clearTimeout(resizeTimer.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobileMax, tabletMax])

    const flags = useMemo(() => ({
        isMobile: deviceType === 'mobile',
        isTablet: deviceType === 'tablet',
        isDesktop: deviceType === 'desktop'
    }), [deviceType])

    return {
        deviceType,
        ...flags,
        width
    }
}
